import { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * Site Media Registry — client cache.
 *
 * Fetches the entire published-only slot map once (small payload: ~15
 * key/url pairs) at app boot, caches in a React context, and exposes:
 *
 *   const { url, alt, isFallback } = useSiteMedia("home.hero", {
 *     fallbackUrl: HERO_FALLBACK, fallbackAlt: "Sunrise"
 *   });
 *
 * The `fallbackUrl` prop is what the component would have hard-coded
 * pre-registry — it stays in the tree as a belt-and-suspenders default
 * for the case where the registry endpoint hasn't answered yet, or the
 * founder points a slot at a draft/archived asset (server returns the
 * fallback URL already, but this local backup means the image never
 * flashes empty on first paint).
 *
 * Not persisted between navigations — kept in memory only. The registry
 * is small and read-once; if the founder swaps an asset the next full
 * page load picks it up, which is the desired user-observable behaviour
 * (no stale service worker cache to bust).
 */

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const SiteMediaContext = createContext({ slots: {}, ready: false });

export function SiteMediaProvider({ children }) {
  const [slots, setSlots] = useState({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/site-media`, { cache: "no-store" });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setSlots(data?.slots || {});
      } catch (_) {
        // Silent: the fallbackUrl prop keeps the UI intact.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => ({ slots, ready }), [slots, ready]);
  return (
    <SiteMediaContext.Provider value={value}>{children}</SiteMediaContext.Provider>
  );
}

/**
 * Pure resolver used by the hook — extracted so it can be unit-tested
 * without a React tree. Given a `slots` map from the server (`{slot:
 * {url, alt, source, content_id}}`) plus a slot key and local
 * `{fallbackUrl, fallbackAlt}`, decide what URL/alt to render.
 *
 * Rules:
 *   • Registry entry with a truthy URL → use it (alt from registry, or
 *     local `fallbackAlt` when registry alt is empty). `isFallback` is
 *     driven by whether the SERVER used a fallback (source !== "vault").
 *   • No entry or entry has empty URL → local fallback URL + alt.
 *     `isFallback: true`, `source: "local_fallback"`.
 */
export function resolveSiteMedia(slots, slot, { fallbackUrl = "", fallbackAlt = "" } = {}) {
  const entry = slots && slots[slot];
  if (entry && entry.url) {
    return {
      url: entry.url,
      alt: entry.alt || fallbackAlt,
      contentId: entry.content_id || null,
      source: entry.source || null,
      isFallback: entry.source !== "vault",
    };
  }
  return {
    url: fallbackUrl,
    alt: fallbackAlt,
    contentId: null,
    source: "local_fallback",
    isFallback: true,
  };
}

export function useSiteMedia(slot, opts = {}) {
  const { slots } = useContext(SiteMediaContext);
  return resolveSiteMedia(slots, slot, opts);
}
